import { Box, Flex } from '@chakra-ui/layout';
import {
  Button,
  FormControl,
  FormErrorMessage,
  IconButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  NumberInput,
  NumberInputField,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import currency from '@common/utility/currency';
import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useSurveyBannerModel } from '../models/SurveyBannerModel';

type NetWorthProps = {
  modalProps: UseDisclosureProps;
};

type NetWorthForm = {
  netWorth: number;
};

export const NetWorth: React.FC<NetWorthProps> = ({ modalProps }) => {
  const model = useSurveyBannerModel();
  const { investorAccount, updateNetWorth } = useInvestorAccountModel();
  const form = useForm<NetWorthForm>({
    defaultValues: {
      netWorth: investorAccount?.non_accredited_survey_v2?.net_worth?.value ?? undefined,
    },
    mode: 'onChange',
  });
  const netWorth = form.watch('netWorth');
  const focusRef = useRef<HTMLInputElement>(null);

  const submitNetWorth = async (netWorth: number) => {
    if (netWorth === investorAccount?.non_accredited_survey_v2?.net_worth?.value) {
      model.nextStep();
    } else {
      model.setLoading(true);
      const status = await updateNetWorth(netWorth);
      if (status.success) {
        model.nextStep();
        model.setLoading(false);
      }
      model.setLoading(false);
    }
  };

  useEffect(() => {
    focusRef.current?.focus();
  }, []);

  return (
    <ModalContent>
      <ModalHeader
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottomWidth={'1px'}
        borderBottomColor={'gray.300'}>
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="chevron-left" />}
          onClick={() => model.prevStep()}
        />
        Net Worth
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="times" />}
          onClick={modalProps.onClose}
        />
      </ModalHeader>
      <ModalBody>
        <Flex flex={1} gap="24px" p={'24px'} justifyContent={'space-between'} flexDirection={'column'} display={'flex'}>
          <Box>
            <TextBlock weight="bold" fontSize={'20px'} lineHeight={'20px'} mb="10px">
              What’s your current net worth?
            </TextBlock>
            <TextBlock fontSize={'14px'}>
              All of your assets outside of your primary home like cash, investments, etc.
            </TextBlock>
          </Box>

          <FormControl isDisabled={model.loading} isInvalid={!!form.formState.errors.netWorth}>
            <Controller
              control={form.control}
              name="netWorth"
              rules={{
                max: {
                  message: 'Net worth must be less than or equal to $2,147,483,647',
                  value: 2147483647,
                },
                min: 0,
              }}
              render={({ field }) => (
                <NumberInput
                  inputMode="numeric"
                  precision={0}
                  isRequired={true}
                  onChange={field.onChange}
                  isValidCharacter={(character) => !isNaN(Number(character))}
                  value={
                    field.value != null ? currency.format(field.value ?? 0, { maximumFractionDigits: 0 }) : undefined
                  }
                  step={5000}>
                  <NumberInputField
                    onKeyUp={(e) => {
                      if (e.key === 'Enter' && Boolean(netWorth)) {
                        submitNetWorth(netWorth);
                      }
                    }}
                    autoFocus={true}
                    placeholder={'$0.00'}
                    maxLength={20}
                    ref={focusRef}
                  />
                </NumberInput>
              )}
            />
            {form.formState.errors.netWorth && (
              <FormErrorMessage>{form.formState.errors.netWorth.message}</FormErrorMessage>
            )}
          </FormControl>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus={true}
          isLoading={model.loading}
          isDisabled={false}
          w={'100%'}
          rightIcon={<Icon icon="chevron-right" />}
          mt={'10px'}
          onClick={() => submitNetWorth(netWorth)}>
          Continue
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
